import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Star,
  Shield,
  Heart,
  Activity,
  Clock,
  Award,
  Users,
  Eye,
  TrendingUp,
  Zap,
  ChevronDown,
  ArrowRight,
  Sparkles,
  Calendar,
  Target,
  Play,
} from 'lucide-react';

/**
 * SalesPage
 *
 * A long-form, conversion-focused landing page for ElderFit Secrets.
 * Rendered inside the global App layout (Navbar + Footer are provided by App.js).
 */
// TAKE5 "Warm Up" workout hero. Click-to-play: we show a real thumbnail with a
// Play button and, on click, swap in the Cloudflare Stream iframe (autoplay) so
// the video plays inline instead of redirecting to sign-up.
//
// NOTE: This video currently has "Require signed URLs" enabled on Cloudflare
// Stream, so the public /iframe embed returns HTTP 401 and the player will show
// an error until signed URLs are disabled (or the video is set to public) on the
// Cloudflare Stream dashboard for videoId 0cc7cdb238099c37b1fcecdbd4d30351.
const TAKE5_VIDEO_ID = '0cc7cdb238099c37b1fcecdbd4d30351';
const TAKE5_STREAM_DOMAIN = 'customer-apz9t94a2fzrv1a9.cloudflarestream.com';
const TAKE5_POSTER_IMAGE = '/images/thumbnails/take5-warmup-thumbnail.svg';
const TAKE5_IFRAME_SRC = `https://${TAKE5_STREAM_DOMAIN}/${TAKE5_VIDEO_ID}/iframe?autoplay=true`;

// On-brand senior-fitness photography. Every URL below is already referenced by
// the workout data (src/data/workouts/elite.js & premium.js), so they are
// known-good Cloudinary assets that load reliably.
const IMG_PROBLEM =
  'https://res.cloudinary.com/dpy2y99bz/image/upload/c_fill,g_face,h_600,w_800/v1762691060/image_4_lnsmrm.jpg';
const IMG_LIFESTYLE_BAND =
  'https://res.cloudinary.com/dpy2y99bz/image/upload/v1762733339/image_uf10ly.jpg';

const SalesPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const painPoints = [
    'Getting up from a chair or off the floor feels harder than it used to',
    'You worry about losing your balance — or having a fall',
    'Generic gym workouts feel unsafe, painful, or built for 25-year-olds',
    'You want to stay independent and keep up with your grandkids',
  ];

  const steps = [
    {
      icon: <Target className="h-7 w-7" />,
      title: '1. Start free in 60 seconds',
      text: 'Create your free account — no credit card required — and jump into your first 5-minute standing workout today.',
    },
    {
      icon: <Calendar className="h-7 w-7" />,
      title: '2. Follow guided programs',
      text: 'Move through progressive, senior-safe routines that grow with you, from gentle to active, at your own pace.',
    },
    {
      icon: <TrendingUp className="h-7 w-7" />,
      title: '3. Get stronger every week',
      text: 'Track your streaks and progress, celebrate real wins, and feel the difference in everyday life.',
    },
  ];

  // Full class strings are used (not interpolated) so Tailwind keeps them at build time.
  const features = [
    {
      icon: <Clock className="h-7 w-7" />,
      title: '5-Minute Workouts',
      text: 'Short, standing, no-equipment follow-alongs that fit any schedule. No time excuses — just press play and move.',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600',
      featured: true,
    },
    {
      icon: <Activity className="h-7 w-7" />,
      title: 'Progressive Programs',
      text: 'A beginner-to-advanced ladder from Gentle Cardio to Core Mastery, so you keep improving safely without plateauing.',
      iconBg: 'bg-emerald-100',
      iconText: 'text-emerald-600',
    },
    {
      icon: <Award className="h-7 w-7" />,
      title: 'Real Functional Strength',
      text: 'Build the strength that keeps you lifting groceries, climbing stairs, and staying independent for years to come.',
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600',
    },
    {
      icon: <Shield className="h-7 w-7" />,
      title: 'Safety-First Coaching',
      text: '"Muscles, not momentum" slow-tempo cues, pain-free ranges, and rest guidance woven into every single movement.',
      iconBg: 'bg-rose-100',
      iconText: 'text-rose-600',
    },
    {
      icon: <TrendingUp className="h-7 w-7" />,
      title: 'Progress Tracking',
      text: 'Streaks, weekly goals, achievement badges, and visual progress so you can actually see yourself getting stronger.',
      iconBg: 'bg-violet-100',
      iconText: 'text-violet-600',
    },
    {
      icon: <Users className="h-7 w-7" />,
      title: 'Community & Family',
      text: 'Stay motivated together with a supportive community, plus a family plan for up to 5 members on the Elite plan.',
      iconBg: 'bg-sky-100',
      iconText: 'text-sky-600',
    },
  ];

  const conditions = [
    'Arthritis Management',
    'Balance & Fall Prevention',
    'Post-Surgery Recovery',
    'Cardiac Rehabilitation',
    'Diabetes Management',
    'Osteoporosis Strength',
  ];

  const testimonials = [
    {
      initials: 'ML',
      name: 'Margaret L.',
      age: 'Age 72',
      photo:
        'https://res.cloudinary.com/dpy2y99bz/image/upload/c_fill,g_face,h_600,w_800/v1762693112/image_5_lojspe.jpg',
      photoAlt: 'Smiling active woman in her 70s after a gentle workout',
      quote:
        "I've tried other fitness programs, but none were designed for people my age. ElderFit has exercises I can actually do without pain, and I've seen real improvements in my mobility.",
    },
    {
      initials: 'RJ',
      name: 'Robert J.',
      age: 'Age 68',
      photo:
        'https://res.cloudinary.com/dpy2y99bz/image/upload/c_fill,g_face,h_600,w_800/v1762693562/image_6_gjm9xm.jpg',
      photoAlt: 'Confident older man staying active and moving safely',
      quote:
        'After my hip replacement, I was nervous about exercising again. The clear instructions and gentle pacing gave me the confidence to get moving safely.',
    },
    {
      initials: 'SB',
      name: 'Susan B.',
      age: 'Age 65',
      photo:
        'https://res.cloudinary.com/dpy2y99bz/image/upload/c_fill,g_face,h_600,w_800/v1762694651/image_8_ws2tup.jpg',
      photoAlt: 'Cheerful woman in her 60s exercising at home',
      quote:
        'I love that I can do these workouts in my living room. Seeing my streak grow every week keeps me coming back — it has become the best part of my morning.',
    },
  ];

  const plans = [
    {
      name: 'Basic',
      price: 'Free',
      cadence: '/forever',
      description: 'Get started with beginner-friendly workouts',
      features: [
        '12 Core Exercises',
        '6 TAKE5 Workouts',
        'Fit Test Ebook',
        'Basic safety guidelines',
        'Simple progress tracking',
      ],
      cta: 'Start Free',
      to: '/register',
      highlight: false,
    },
    {
      name: 'Premium',
      price: '$19',
      cadence: '/month',
      description:
        'Transform your fitness journey with daily challenges, monthly guided programs, and comprehensive progress tracking.',
      features: [
        'Everything in Basic',
        'Daily Challenges',
        'Monthly Programs',
        'Progress Tracking',
        'WOW Videos',
      ],
      cta: 'Upgrade to Premium',
      to: '/subscription/plans',
      highlight: true,
    },
    {
      name: 'Elite',
      price: '$39',
      cadence: '/month',
      description:
        'Experience the ultimate fitness solution with professional trainer-led sessions, AI-powered form analysis, and family progress tracking.',
      features: [
        'Everything in Premium',
        'Elite Guided Sessions',
        'Family Management',
        'AI Form Analysis',
        'Safety Monitoring',
        'Advanced safety features',
        'Exclusive content',
        'Custom routine builder with AI guidance',
      ],
      cta: 'Upgrade to Elite',
      to: '/subscription/plans',
      highlight: false,
    },
  ];

  const faqs = [
    {
      q: 'Do I need any equipment or experience?',
      a: 'Not at all. Our core workouts are standing, no-equipment follow-alongs designed for complete beginners. Everything is coached slowly and clearly, and you move entirely at your own pace.',
    },
    {
      q: 'Is it safe if I have a health condition?',
      a: 'Safety is built into everything we do, with pain-free-range cues and rest guidance on every exercise. Our Elite plan also includes programs designed around specific conditions like arthritis, osteoporosis, and post-surgery recovery. As always, check with your doctor before starting any new exercise program.',
    },
    {
      q: 'How much does it cost to start?',
      a: 'Nothing. The Basic plan is completely free and requires no credit card. You can upgrade to Premium or Elite anytime if you want more workouts and features.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Yes. There are no long-term contracts. You can cancel your paid subscription at any time from your account settings.',
    },
    {
      q: 'I am not very tech-savvy. Will I be able to use it?',
      a: 'Absolutely. ElderFit Secrets is designed to be simple and easy to navigate. If you can press play on a video, you can use it — and our support team is here if you ever need a hand.',
    },
  ];

  return (
    <div className="bg-white">
      {/* Announcement bar */}
      <div className="bg-blue-900 text-white text-center text-sm py-2 px-4">
        <span className="font-medium">
          Join thousands of active over-50s — start free today, no credit card required.
        </span>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 text-sm font-medium">
                <Sparkles className="h-4 w-4 text-yellow-300" />
                Fitness for healthy aging
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                Stay Strong, Steady &amp; Independent — In Just 5 Minutes a Day
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-8 max-w-xl">
                Gentle, senior-safe standing workouts designed for active adults over 50.
                Build real strength, improve your balance, and move with confidence.
              </p>

              <div className="max-w-lg mb-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold px-8 py-4 rounded-lg text-lg whitespace-nowrap transition-colors shadow-lg"
                >
                  Start Free
                </Link>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-blue-100">
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-300" /> No credit card required
                </span>
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-300" /> Cancel anytime
                </span>
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-300" /> Beginner friendly
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 sm:p-6 shadow-2xl">
                {/* Responsive 16:9 click-to-play video */}
                <div
                  className="relative w-full rounded-xl overflow-hidden mb-4 bg-blue-950"
                  style={{ paddingBottom: '56.25%' }}
                >
                  {isPlaying ? (
                    <iframe
                      src={TAKE5_IFRAME_SRC}
                      title="TAKE5 Warm Up — 5-minute workout"
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 'none' }}
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsPlaying(true)}
                      aria-label="Play the TAKE5 Warm Up workout video"
                      className="group absolute inset-0 w-full h-full cursor-pointer focus:outline-none focus:ring-4 focus:ring-yellow-300"
                    >
                      <img
                        src={TAKE5_POSTER_IMAGE}
                        alt="TAKE5 Warm Up — free 5-minute workout thumbnail"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Readability + brand gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-blue-900/20 to-transparent" />
                      {/* Play button */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/95 text-blue-700 shadow-xl transition-transform duration-200 group-hover:scale-110 group-focus:scale-110">
                          <Play className="h-8 w-8 sm:h-9 sm:w-9 fill-current translate-x-0.5" />
                        </span>
                      </div>
                      {/* Free badge */}
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-yellow-500 text-blue-900 text-xs font-bold px-3 py-1 rounded-full shadow">
                        <Sparkles className="h-3.5 w-3.5" /> Free workout
                      </span>
                      <span className="absolute bottom-3 left-3 right-3 text-left text-white font-semibold text-sm sm:text-base drop-shadow">
                        TAKE5 · Warm Up · 5 min
                      </span>
                    </button>
                  )}
                </div>
                <p className="text-center text-sm text-blue-100 mb-4">
                  Press play to preview a 5-minute TAKE5 workout ·{' '}
                  <Link
                    to="/register"
                    className="font-semibold text-yellow-300 underline underline-offset-2 hover:text-yellow-200"
                  >
                    Start free
                  </Link>
                </p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">5 min</div>
                    <div className="text-xs text-blue-100">Quick workouts</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">50+</div>
                    <div className="text-xs text-blue-100">Guided routines</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">All</div>
                    <div className="text-xs text-blue-100">Levels welcome</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / stats bar */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { stat: '5 min', label: 'A day is all it takes' },
              { stat: '100%', label: 'Standing, low-impact' },
              { stat: 'AI', label: 'Real-time form checks' },
              { stat: 'Free', label: 'To get started' },
            ].map((item, i) => (
              <div key={i}>
                <div className="text-3xl font-extrabold text-blue-600">{item.stat}</div>
                <div className="text-sm text-gray-600 mt-1">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem / agitation */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Supporting photo */}
            <div className="relative order-first">
              <img
                src={IMG_PROBLEM}
                alt="An older adult moving gently and confidently through a low-impact exercise"
                className="w-full h-64 sm:h-80 lg:h-[26rem] object-cover rounded-2xl shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-5 left-5 right-5 sm:left-6 sm:right-auto sm:max-w-xs bg-white rounded-xl shadow-lg border border-gray-100 px-5 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <Heart className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-medium text-gray-700">
                    Gentle, senior-safe movement you can actually stick with.
                  </p>
                </div>
              </div>
            </div>

            {/* Copy + pain points */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Staying active after 50 shouldn't feel like a battle
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                If any of these sound familiar, you're not alone — and it's not your fault.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-left">
                {painPoints.map((point, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-5"
                  >
                    <span className="mt-0.5 flex-shrink-0 h-6 w-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-sm font-bold">
                      !
                    </span>
                    <span className="text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-800 font-medium mt-8">
                ElderFit Secrets was built to change that — gently, safely, and at your pace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-1.5 mb-4 text-sm font-semibold">
              <Sparkles className="h-4 w-4" /> How it works
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Feel stronger in three simple steps
            </h2>
            <p className="text-lg text-gray-600">
              No gym, no confusion, no pressure — just a clear path to moving better.
            </p>
          </div>
          <div className="relative grid md:grid-cols-3 gap-8">
            {/* Connector line behind the step cards (desktop only) */}
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-9 left-[16.666%] right-[16.666%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200"
            />
            {steps.map((step, i) => (
              <div
                key={i}
                className="relative bg-white rounded-2xl p-8 shadow-sm border border-gray-100 transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
              >
                <div className="flex items-center gap-4 mb-5">
                  <span className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white text-lg font-bold shadow-md ring-4 ring-gray-50">
                    {i + 1}
                  </span>
                  <span className="inline-flex p-3 rounded-xl bg-blue-100 text-blue-600">
                    {step.icon}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {step.title.replace(/^\d+\.\s*/, '')}
                </h3>
                <p className="text-gray-600">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for — lifestyle image band */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl shadow-xl max-w-6xl mx-auto">
            <img
              src={IMG_LIFESTYLE_BAND}
              alt="Active adults over 50 moving with energy and confidence"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/85 via-blue-900/60 to-blue-900/30" />
            <div className="relative px-6 py-14 sm:px-10 md:px-14 md:py-20 max-w-2xl text-white">
              <span className="inline-flex items-center gap-2 bg-yellow-500 text-blue-900 rounded-full px-4 py-1.5 mb-5 text-sm font-bold">
                <Users className="h-4 w-4" /> Made for you
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Built for real people over 50 — not 25-year-old gym-goers
              </h2>
              <p className="text-lg text-blue-100 mb-6">
                Whether you're just getting back into movement or staying sharp for the
                grandkids, every routine meets you exactly where you are — safe, doable, and
                genuinely enjoyable.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-300" /> Beginners welcome
                </span>
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-300" /> Low-impact &amp; joint-friendly
                </span>
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-300" /> Do it from home
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features & benefits */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-white via-blue-50/40 to-white">
        {/* Soft decorative accents */}
        <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-yellow-200/30 blur-3xl" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-700 rounded-full px-4 py-1.5 mb-4 text-sm font-semibold">
              <Award className="h-4 w-4" /> Everything included
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to stay active for life
            </h2>
            <p className="text-lg text-gray-600">
              Thoughtfully designed for older adults — safe, effective, and genuinely enjoyable.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, i) => (
              <div
                key={i}
                className={`group relative rounded-2xl p-7 transition-all duration-200 hover:-translate-y-1.5 ${
                  feature.featured
                    ? 'bg-gradient-to-br from-blue-600 to-blue-800 text-white shadow-xl'
                    : 'bg-white border border-gray-100 shadow-sm hover:shadow-xl'
                }`}
              >
                {feature.featured && (
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1 bg-yellow-500 text-blue-900 text-xs font-bold px-2.5 py-1 rounded-full">
                    <Star className="h-3 w-3 fill-current" /> Most loved
                  </span>
                )}
                <div
                  className={`inline-flex p-3.5 rounded-xl mb-5 transition-transform duration-200 group-hover:scale-110 ${
                    feature.featured ? 'bg-white/15 text-yellow-300' : `${feature.iconBg} ${feature.iconText}`
                  }`}
                >
                  {feature.icon}
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${feature.featured ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={feature.featured ? 'text-blue-50' : 'text-gray-600'}>{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI differentiator */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 text-sm font-medium">
                <Zap className="h-4 w-4 text-yellow-300" /> Elite AI Technology
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-5">
                Like having a personal trainer watching every rep
              </h2>
              <p className="text-lg text-blue-100 mb-8">
                Our Elite plan uses your camera to analyze your form in real time — checking
                posture, movement, and balance — then gives instant, gentle feedback to keep
                you safe and effective.
              </p>
              <ul className="space-y-4">
                {[
                  { icon: <Eye className="h-5 w-5" />, text: 'Real-time form analysis and corrections' },
                  { icon: <Shield className="h-5 w-5" />, text: 'Smart safety monitoring before, during & after' },
                  { icon: <Heart className="h-5 w-5" />, text: 'Personalized recommendations that adapt to you' },
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="flex-shrink-0 p-2 rounded-lg bg-white/10 text-yellow-300">
                      {item.icon}
                    </span>
                    <span className="text-blue-50">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 border border-white/20 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-xl font-semibold mb-6">Condition-specific programs</h3>
              <p className="text-blue-100 mb-6">
                Elite members get guided programs designed around real health needs:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {conditions.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2.5 text-sm"
                  >
                    <Check className="h-4 w-4 text-green-300 flex-shrink-0" />
                    {c}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Loved by members like you
            </h2>
            <div className="flex justify-center gap-1 text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-current" />
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="flex gap-1 text-yellow-400 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.photoAlt || `${t.name}, ElderFit Secrets member`}
                      className="h-12 w-12 rounded-full object-cover mr-4 ring-2 ring-blue-100"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-blue-700 font-bold">{t.initials}</span>
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900">{t.name}</h4>
                    <p className="text-gray-500 text-sm">{t.age}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose the plan that's right for you
            </h2>
            <p className="text-lg text-gray-600">
              Start completely free. Upgrade whenever you're ready — cancel anytime.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto items-start">
            {plans.map((plan, i) => (
              <div
                key={i}
                className={`relative flex h-full flex-col bg-white rounded-2xl p-8 border transition-all ${
                  plan.highlight
                    ? 'border-blue-500 shadow-xl md:-translate-y-2'
                    : 'border-gray-200 shadow-sm'
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500">{plan.cadence}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.to}
                  className={`mt-auto block w-full text-center font-semibold py-3 px-4 rounded-lg transition-colors ${
                    plan.highlight
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee / risk reversal */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-blue-50 border border-blue-100 rounded-2xl p-8 md:p-10 text-center">
            <div className="inline-flex p-4 rounded-full bg-blue-600 text-white mb-5">
              <Shield className="h-8 w-8" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Try it risk-free
            </h2>
            <p className="text-lg text-gray-600">
              Start on the free Basic plan with no credit card and no commitment. If you upgrade,
              you can cancel anytime — no contracts, no hassle. You have nothing to lose and a
              stronger, more confident life to gain.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-gray-600">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-5">
            Your stronger, steadier future starts today
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-8">
            Join thousands of active over-50s improving their strength, balance, and confidence
            with ElderFit Secrets — five minutes at a time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold px-8 py-4 rounded-lg text-lg transition-colors shadow-lg"
            >
              Start Free Today <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/subscription/plans"
              className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/30 font-semibold px-8 py-4 rounded-lg text-lg transition-colors"
            >
              Compare Plans
            </Link>
          </div>
          <p className="text-sm text-blue-100 mt-6">
            No credit card required · Cancel anytime · Beginner friendly
          </p>
        </div>
      </section>
    </div>
  );
};

export default SalesPage;
